import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Trash2, ClipboardCopy } from "lucide-react";
import { toast } from "sonner";

/**
 * Render an AI-generated scout report body into structured sections.
 * The model outputs markdown-style headers (# HEADING or HEADING).
 * We split on ALL-CAPS section names and render each block.
 */
function renderReportBody(text) {
  if (!text) return null;
  // Normalize: remove leading "# " and stray markdown.
  const cleaned = text.replace(/^#+\s*/gm, "").replace(/\*\*/g, "");
  // Split into blocks on blank line, then group by section header pattern (line is ALL CAPS + spaces).
  const lines = cleaned.split("\n");
  const sections = [];
  let current = null;
  for (const raw of lines) {
    const line = raw.trimEnd();
    const isHeader = /^[A-Z][A-Z0-9 &/'\-]{4,}$/.test(line.trim()) && !line.trim().startsWith("-");
    if (isHeader) {
      if (current) sections.push(current);
      current = { title: line.trim(), body: [] };
    } else {
      if (!current) current = { title: "REPORT", body: [] };
      current.body.push(line);
    }
  }
  if (current) sections.push(current);
  return sections;
}

export default function ScoutReport() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/scout-reports/${id}`);
        setReport(data);
      } catch (e) { setErr("Report not found"); }
    })();
  }, [id]);

  const copy = () => {
    navigator.clipboard.writeText(report?.body || "");
    toast.success("Report copied to clipboard");
  };

  const del = async () => {
    if (!window.confirm("Delete this scout report?")) return;
    await api.delete(`/scout-reports/${id}`);
    toast.success("Deleted");
    nav("/film-hub");
  };

  if (err) return <div className="p-8 text-red-400">{err}</div>;
  if (!report) return <div className="p-8 text-slate-400">Loading scout report…</div>;

  const sections = renderReportBody(report.body);

  return (
    <div className="practice-print min-h-screen bg-white text-black">
      {/* Screen-only toolbar */}
      <div className="print-hide bg-[#0B0D12] text-white border-b border-surface-3 px-8 py-4 flex items-center justify-between gap-3">
        <button onClick={() => nav(-1)} data-testid="scout-back" className="flex items-center gap-2 text-sm text-slate-300 hover:text-amber font-condensed uppercase tracking-widest">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="font-condensed uppercase tracking-widest text-sm text-amber flex-1 text-center">
          Scout Report · {report.opponent}
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={copy} data-testid="scout-copy" variant="outline"
            className="rounded-none border-surface-3 hover:border-amber hover:text-amber h-9 text-[11px] font-condensed uppercase tracking-widest">
            <ClipboardCopy className="w-3 h-3 mr-1" /> Copy
          </Button>
          <Button onClick={del} data-testid="scout-delete" variant="ghost"
            className="rounded-none hover:text-red-400 h-9 text-[11px] font-condensed uppercase tracking-widest">
            <Trash2 className="w-3 h-3 mr-1" /> Delete
          </Button>
          <Button onClick={() => window.print()} data-testid="scout-print"
            className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] h-9 text-[11px] font-condensed uppercase tracking-widest">
            <Printer className="w-3 h-3 mr-1" /> Print / Save PDF
          </Button>
        </div>
      </div>

      {/* Printable body */}
      <div className="practice-card mx-auto my-6 bg-white text-black shadow-lg print:shadow-none print:my-0" style={{ maxWidth: "820px", padding: "40px 44px" }}>
        {/* Header */}
        <div className="border-b-2 border-black pb-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="font-condensed uppercase tracking-[0.35em] text-xs text-neutral-500">{user?.program_name || "Your Program"} · Scout Report</div>
              <div className="font-display text-5xl uppercase tracking-wide leading-none mt-1">vs. {report.opponent}</div>
            </div>
            <div className="text-right text-xs font-condensed uppercase tracking-widest text-neutral-600">
              <div>Generated {new Date(report.created_at).toLocaleDateString()}</div>
              <div className="mt-1">{report.clip_count} clip{report.clip_count !== 1 ? "s" : ""} on file</div>
              {report.created_by && <div className="mt-1">By {report.created_by}</div>}
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="mt-6 space-y-6">
          {sections.map((s, i) => (
            <div key={s.title} className="avoid-page-break" data-testid={`scout-section-${i}`}>
              <div className="font-display text-2xl uppercase tracking-wider border-b-2 border-black pb-1">{s.title}</div>
              <div className="mt-3 text-sm leading-relaxed whitespace-pre-wrap">
                {s.body.join("\n").trim()}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-10 pt-3 border-t border-black flex items-center justify-between text-[10px] font-condensed uppercase tracking-widest text-neutral-600">
          <div>Gridiron OS · Scout Report</div>
          <div>Confidential — Coaches Only</div>
        </div>
      </div>
    </div>
  );
}
